import { animate, state, style, transition, trigger } from '@angular/animations';
import { A11yModule } from '@angular/cdk/a11y';
import { CdkOverlayOrigin, OverlayModule, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatRippleModule } from '@angular/material/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ShebaGtmEventDirective, useShebaGtmService } from 'app/core/services/google-tag-manager/gtm.directive';
import { CategoryIllustrationsIcon } from 'assets/icons/category-illustrations/category-illustrations';
import { CloseIcon } from 'assets/icons/close/close';
import { SearchIcon } from 'assets/icons/search/search';
import { Subscription, switchMap } from 'rxjs';
import { containerTypes } from '../../../app.routes';
import { MultiSelectComponent } from '../../../core/components/multi-select/multi-select.component';
import { API_MAP } from '../../../core/helpers/api.map';
import { SearchResultService } from '../../../pages/search-result/services/search-result.service';
import { ShebaButtonComponent } from '../button/button.component';
import { SearchAreaComponent } from '../search-area/search-area.component';
import { environment } from 'environments/environment';
import { SEARCH_CONSTANTS } from '../../../core/constants/search.constants';
import { LanguageService } from 'app/shared/services/language.service';

@Component({
  selector: 'sheba-search-header',
  templateUrl: 'search-header.component.html',
  imports: [
    OverlayModule,
    A11yModule,
    NgClass,
    MatRippleModule,
    SearchIcon,
    ShebaButtonComponent,
    TranslateModule,
    CloseIcon,
    CategoryIllustrationsIcon,
    FormsModule,
    SearchAreaComponent,
    MultiSelectComponent,
    ShebaGtmEventDirective
  ],
  host: {
    class: 'contents'
  },
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('openClose', [
      state(
        '*',
        style({
          opacity: 1,
          height: '*'
        })
      ),
      state(
        'void',
        style({
          opacity: 0.0,
          transform: 'translateY(-10%)'
        })
      ),
      transition('* => void', [animate('100ms')]),
      transition('void => *', [animate('150ms')])
    ])
  ]
})
export class ShebaSearchHeaderComponent {
  overlayOrigin = input.required<CdkOverlayOrigin>();
  isOpen = model(false);
  pageTypes = signal<{ name: string; id: string }[]>([]);
  subscription = new Subscription();
  #searchService = inject(SearchResultService);
  #route = inject(Router);
  blockScrollStrategy = inject(ScrollStrategyOptions).reposition();
  languageService = inject(LanguageService);
  searchPageRoute = containerTypes.find((container) => container.ContainerAlias === 'globalSearchPage')?.ContainerUrl;
  indexName = environment.elasticsearch.indexes.generalContent;
  searchFields = SEARCH_CONSTANTS.SEARCH_FIELDS;
  readonly #gtm = useShebaGtmService();
  readonly #translate = inject(TranslateService);

  get mutableSearchFields(): string[] {
    return [...this.searchFields];
  }

  constructor() {
    effect(
      () => {
        if (this.isOpen()) {
          this.subscription.add(
            this.#searchService
              .get(API_MAP.globalSearchPage + this.searchPageRoute)
              .pipe(switchMap((data) => this.#searchService.getSearchFilters(data.content.pageTypesContainerPath)))
              .subscribe((f: any) => {
                this.pageTypes.set(f.filters.map((x: any) => ({ name: x.content.title, id: x.content.id })));
              })
          );
        }
      },
      { allowSignalWrites: true }
    );
  }
  model: { search: string; category: { name: string; id: string }[] } = {
    search: '',
    category: []
  };

  submit() {
    this.isOpen.set(false);
    const categoryNames = this.model.category.map(cat => cat.name).join(', ');

    this.#gtm.pushEvent({
      eventInfo: {
        user_action: 'user_search_' + this.model.search,
        search_term: this.model.search,
        click_text: this.#translate.instant('HEADER.SEARCH_AREA.SEARCH'),
        tags: categoryNames
      }
    });

    // Extract just the path without query params or trailing slash
    const currentPath = this.#route.url.split('?')[0].replace(/\/$/, '');
    const targetPath = this.searchPageRoute?.replace(/\/$/, '') || '';

    // Always navigate with queryParams, but handle differently based on location
    const currentLang = this.languageService.currentLanguage().prefix;
    if(currentLang === 'en'){
      this.searchPageRoute=  containerTypes.find((r) => r.ContainerAlias === 'globalSearchPage' && r.ContainerUrl.startsWith('/en/'))?.ContainerUrl;
    }
    if (currentPath === targetPath) {
      // Force navigation by navigating away and back
      this.#route.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.#route.navigate([this.searchPageRoute], {
          state: { pageTypes: this.model.category },
          queryParams: { term: this.model.search }
        }).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      });
    } else {
      this.#route.navigate([this.searchPageRoute], {
        state: { pageTypes: this.model.category },
        queryParams: { term: this.model.search }
      });
    }
  }
}
